import {
    ClientCampaignTiktokPostStats,
    ClientCampaignTiktokPostStatsTotals,
    getClientCampaignPostTiktokStats,
} from '@round/api';
import { useCallback, useState } from 'react';
import { showNotification } from '../../../../../utils/showNotification';

export default function useCampaignPostStats(campaignId: number | null, tiktokCreatorsCount: number) {
    const [postStats, setPostStats] = useState<ClientCampaignTiktokPostStats[]>([]);
    const [postStatsTotals, setPostStatsTotals] = useState<ClientCampaignTiktokPostStatsTotals | null>(null);

    const [isLoading, setIsLoading] = useState(false);
    const [hasErrorLoading, setHasErrorLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    const fetchPostStats = useCallback(
        async (campaignId: number, tiktokCreatorsCount: number, requestInit?: RequestInit) => {
            try {
                if (tiktokCreatorsCount <= 0) {
                    return;
                }
                setHasErrorLoading(false);
                setIsLoading(true);
                const response = await getClientCampaignPostTiktokStats(
                    {
                        campaignId,
                        page_size: tiktokCreatorsCount,
                    },
                    requestInit
                );

                if (response.status === 404) {
                    throw new Error(response.data.detail);
                }

                setPostStats(response.data.results);
                setPostStatsTotals(response.data.totals);
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                setHasErrorLoading(true);
                showNotification((e as Error).message, 'error');
                throw e;
            } finally {
                setIsLoading(false);
            }
        },
        []
    );

    const init = useCallback(
        (requestInit?: RequestInit) => {
            if (typeof campaignId === 'number') {
                fetchPostStats(campaignId, tiktokCreatorsCount, requestInit).then(() => setIsInitialized(true));
            }
        },
        [campaignId, fetchPostStats, tiktokCreatorsCount]
    );

    return {
        postStats,
        postStatsTotals,
        isLoading,
        hasErrorLoading,
        isInitialized,
        init,
    };
}
